/// <amd-module name="Dashboards/WidgetHost/HostImplementation/WidgetHostImplementationBase" />
import { WidgetCatalogQuery } from "Dashboards/WidgetCatalog/WidgetCatalogQuery";
import { getProviderInfo, isInternalContribution, ProviderInfo } from "Dashboards/WidgetCatalog/WidgetContributionsHelper";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { WidgetContributionRenderingProps } from "Dashboards/WidgetContracts/Internal/WidgetContributionTypes";
import { WidgetCopyProps, WidgetHostLoadOutcome, WidgetHostProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import {
    LoadedEventInfo,
    LoadingStatus,
    WidgetContextMenuColorMode,
    WidgetPresentationMode,
    WidgetProps,
    WidgetSize
} from "Dashboards/WidgetContracts/WidgetProps";
import * as Resources from "Dashboards/WidgetHost/Resources";
import * as React from "react";
import { Lightbox } from "Reporting/Lightbox/Lightbox";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { css, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";
import { Button } from "VSSUI/Button";
import { KeyCode } from "VSSUI/Util";
import { IVssContextualMenuItem } from "VSSUI/Components/VssContextualMenu";
import { Spinner } from "VSSUI/Spinner";
import { IDashboardsPanelManager } from "../Helpers/DashboardsPanelManager";
import { generateWidgetProps, IWidgetHostCallbacks } from "../Helpers/PresentationContextHelpers";
import {
    publishWidgetHostInteraction,
    publishWidgetHostLoadFailed,
    publishWidgetHostLoadStarted,
    traceWidgetHostWarning,
    WidgetTelemetryInformation
} from "../Helpers/WidgetHostTelemetry";
import { DefaultWidgetFailedMessage } from "../MessageComponents/DefaultWidgetFailedMessage";
import { ExtensionFailedMessage } from "../MessageComponents/ExtensionFailedMessage";
import { WidgetErrorMessage } from "../MessageComponents/WidgetErrorMessage";
import { WidgetHostEditOverlay } from "../OverlayComponents/WidgetHostEditOverlay";
import { WidgetHostOverlay } from "../OverlayComponents/WidgetHostOverlay";
import { getUpdatedWidgetPosition } from "Dashboards/WidgetHost/HostImplementation/KeyboardHandler"

export interface WidgetHostCommonState {
    isMouseInsideWidget: boolean;
    widgetContextMenuColorMode: WidgetContextMenuColorMode | undefined;
    customMenuItems?: IVssContextualMenuItem[];

    // False by default. Enabled after a short grace period to avoid an over-busy render experience.
    readyForLoadingSpinner: boolean;

    // Used to track if the widgetHost has recorded an initial load to the consumer of the host.
    hasSignaledFirstLoad: boolean;
    widgetLoadStatus: LoadingStatus;
    widgetLoadOutcomeDescription: string | undefined;
}

export interface WidgetHostImplementationProps extends WidgetHostProps {
    /**
     * Core Props for Widget Rendering
     */
    widgetContributionRenderingProps: WidgetContributionRenderingProps;

    isWidgetConfigurable: boolean;
}

/**
 * Responsible for version agnostic concerns of Hosting a Widget. This responsability makes sense to transition up to the common "WidgetHost" component.
 * -Handles context menu, edit/delete and lightbox options for widgets which have pertinent interactivity options enabled.
 * -Handles Loading spinner and error rendering.
 *
 * Delegates Lightbox & Contribution based rendering to underlying widget. We'll likely split these roles through contained impl's of strategy pattern in later iteration.
 */
export abstract class WidgetHostImplementationBase<P extends WidgetHostImplementationProps> extends VssComponent<P, WidgetHostCommonState> {
    private static widgetClientTimeoutInMS = 120 * 1000;
    private static spinnerStartTimeoutInMS = 500;
    private contentContainerElement: HTMLDivElement;
    private editOverlayElement: WidgetHostEditOverlay;
    // Tracks timer for widget load events. Aside from early dispose - it stops on explicit load signal, timeout or uncaught exception.
    private hostTimeoutId: number;

    // Allows for ignoring incoming promise communications from legacy implementations coming in after the widget is unmounted.
    // This needs to be checked before making state changes coming in on async signals.
    private isUnmounted: boolean;
    private _moreActionsButton: Button | null;

    /** Renders a modern widget in the lightbox. */
    protected abstract onShowLightbox: () => void;

    public constructor(props: P, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            isMouseInsideWidget: false,
            readyForLoadingSpinner: false,
            hasSignaledFirstLoad: false,
            widgetLoadStatus: LoadingStatus.loading,
            widgetLoadOutcomeDescription: undefined,
            widgetContextMenuColorMode: undefined
        };
    }

    public componentDidMount() {
        super.componentDidMount();

        publishWidgetHostLoadStarted(this.context.pageContext, this.getWidgetTelemetryInfo());

        this.hostTimeoutId = this.setTimeout(this.onLoadTimeout, WidgetHostImplementationBase.widgetClientTimeoutInMS);
        this.setTimeout(this.onSpinnerAllowedTimeout, WidgetHostImplementationBase.spinnerStartTimeoutInMS);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.isUnmounted = true;
    }

    public componentWillReceiveProps(newProps: WidgetHostImplementationProps) {
        // On Widget props changes, re-classify as loading, but leave the first load signal state unchanged.
        if (JSON.stringify(newProps.widgetProps) != JSON.stringify(this.props.widgetProps)) {
            this.setState({
                widgetLoadStatus: LoadingStatus.loading,
                widgetLoadOutcomeDescription: undefined
            });
        }
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.onFirstLoadTransition(WidgetHostLoadOutcome.UnhandledFailure);
        if (this.state.widgetLoadStatus === LoadingStatus.loading && !this.isUnmounted) {
            if (!this.state.hasSignaledFirstLoad) {
                publishWidgetHostLoadFailed(this.context.pageContext, this.getWidgetTelemetryInfo(), {
                    errorType: WidgetHostLoadOutcome[WidgetHostLoadOutcome.UnhandledFailure],
                    error: error
                });
            }

            this.setState({
                hasSignaledFirstLoad: true,
                widgetLoadStatus: LoadingStatus.failure,
                widgetLoadOutcomeDescription: `Widget ${this.props.widgetProps.contributionId} failed to load with an unhandled exception. ${error.message}`
            });
        }
    }

    public render(): JSX.Element {
        return (
            <div
                className={css("widget-host", this.getModeClass())}
                style={this.getRenderSize()}
                onMouseEnter={this.onMouseEnterWidget}
                onMouseLeave={this.onMouseLeaveWidget}
            >
                {this.renderWidgetHostContent()}
                {this.renderOverlay()}
            </div>
        );
    }

    protected abstract getHostType(): string;

    /** This path is used by the Disabled WidgetHostImplementation to:
     * 1-Notify parent of load completion
     * 2-End loading state
     * 3-Publish WidgetHost telemetry  */
    protected setPredeterminedOutcome(predeterminedOutcome: WidgetHostLoadOutcome): void {
        this.onFirstLoadTransition(predeterminedOutcome);
        if (this.state.widgetLoadStatus === LoadingStatus.loading) {
            if (!this.state.hasSignaledFirstLoad) {
                publishWidgetHostLoadFailed(this.context.pageContext, this.getWidgetTelemetryInfo(), {
                    errorType: WidgetHostLoadOutcome[predeterminedOutcome],
                    error: "Widget was disabled, in the fashion described by errorType."
                });
            }

            this.setState({
                hasSignaledFirstLoad: true,
                widgetLoadStatus: LoadingStatus.success
            });
        }
    }

    protected getErrorContent(): Promise<JSX.Element> {
        const contributionId = this.props.widgetProps.contributionId;

        if (!isInternalContribution(contributionId)) {
            return getProviderInfo(this.context.pageContext, contributionId).then((providerInfo: ProviderInfo) => {
                return <ExtensionFailedMessage extensionName={providerInfo.providerDisplayName} extensionUrl={providerInfo.marketplaceUrl} />;
            });
        } else {
            return Promise.resolve(<DefaultWidgetFailedMessage errorText={this.state.widgetLoadOutcomeDescription || ""} />);
        }
    }

    protected showErrorDialog = (): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.getErrorContent().then((errorContent) => {
            layoutManager.renderCallout((onDismiss: () => void) => {
                return (
                    <Lightbox onDismiss={onDismiss} title={Resources.WidgetLoadError_DialogTitle} retainDefaultLayout={true}>
                        {errorContent}
                    </Lightbox>
                );
            });
        });
    };

    protected renderWidgetHostContent(): JSX.Element {
        // Add run-time context information to the Widget Properties.

        const handlers: IWidgetHostCallbacks = {
            onWidgetLoaded: this.onWidgetLoadComplete,
            onConfigure: this.onConfigureWidget,
            setMenuColorMode: this.setMenuColorMode,
            setCustomContextMenuItems: this.setCustomContextMenuItems
        };

        // Compute Runtime Widget properties covering rendering hints such as link handling and animation guidance
        const widgetProps: WidgetProps = generateWidgetProps(this.props.widgetPresentationMode, this.props, handlers, this.props.convertToPixelSize);
        widgetProps.size = this.getRenderSize();

        // Note:
        // - Widget spinner shows during first load
        // - Error experience shows in failure mode
        // - Widget is always rendered, but only visible after first load in a non-failure state

        const isWidgetLoadingForFirstTime = !this.state.hasSignaledFirstLoad && this.state.widgetLoadStatus === LoadingStatus.loading;
        const hideWidget = !this.state.hasSignaledFirstLoad || this.state.widgetLoadStatus === LoadingStatus.failure;
        return (
            <div className="widget-container">
                {isWidgetLoadingForFirstTime && this.state.readyForLoadingSpinner && this.renderLoadingComponent()}
                {this.state.widgetLoadStatus === LoadingStatus.failure && this.renderFailedComponent()}
                <div className={css("content-container ", hideWidget ? "invisible" : "")} ref={(ref: HTMLDivElement) => { this.contentContainerElement = ref }} onKeyDown={this.onKeydown.bind(this)}>
                    {this.state.widgetLoadStatus !== LoadingStatus.failure ? this.renderWidgetHostImplementation(widgetProps) : null}
                </div>
            </div>
        );
    }

    protected abstract renderWidgetHostImplementation(widgetProps: WidgetProps): JSX.Element;
    protected abstract getRenderSize(): WidgetSize;

    protected canUseLightbox(): boolean {
        return this.props.widgetContributionRenderingProps.lightbox != null;
    }

    protected canConfigureWidget(): boolean {
        return !!(this.props.userCanConfigure && this.props.isWidgetConfigurable);
    }

    protected canDeleteWidget(): boolean {
        return this.props.userCanConfigure!;
    }

    protected canCopyWidget(): boolean {
        return true;
    }

    /** Determines if the user can see  an "about this widget" menu option for more information about the underlying widget. */
    protected canViewWidgetInformation(): boolean {
        const featureIsEnabled = isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards.dashboard-widget-help-feature", false);
        return featureIsEnabled && this.props.widgetProps.isEnabled;
    }

    // This method allows for recognition of mal-formed widget load event information, which do not conform to documented contract.
    protected normalizeEventInfo(eventInfo: LoadedEventInfo): LoadedEventInfo {
        return eventInfo;
    }

    private onLoadTimeout = (): void => {
        if (this.state.widgetLoadStatus === LoadingStatus.loading && !this.isUnmounted) {
            this.onFirstLoadTransition(WidgetHostLoadOutcome.Timeout);
            if (!this.state.hasSignaledFirstLoad) {
                publishWidgetHostLoadFailed(this.context.pageContext, this.getWidgetTelemetryInfo(), {
                    errorType: WidgetHostLoadOutcome[WidgetHostLoadOutcome.Timeout],
                    error: "The widget timed out."
                });
            }

            this.setState({
                hasSignaledFirstLoad: true,
                widgetLoadStatus: LoadingStatus.failure,
                widgetLoadOutcomeDescription: `Widget ${this.props.widgetProps.contributionId} did not load within allowed time.`
            });
        }
    };

    private getWidgetTelemetryInfo(): WidgetTelemetryInformation {
        return {
            widgetHostType: this.getHostType(),
            contributionId: this.props.widgetProps.contributionId,
            widgetId: this.props.widgetProps.id,
            presentationMode: this.props.widgetPresentationMode,
            parentPageName: this.props.parentPageName
        };
    }

    private onSpinnerAllowedTimeout = (): void => {
        this.setState({
            readyForLoadingSpinner: true
        });
    };

    private renderLoadingComponent = (): JSX.Element => {
        return <Spinner className="absolute-fill" />;
    };

    private renderFailedComponent = (): JSX.Element => {
        return (
            <WidgetErrorMessage
                widgetName={this.props.widgetProps.name}
                size={this.getRenderSize()}
                onClick={this.showErrorDialog}
                hideTitle={this.props.shouldNotShowTitle}
            />
        );
    };

    /** Applies Mode-specific classes for the Widget Host for presentational variations, such as Z-index during Preview.*/
    private getModeClass(): string | null {
        return this.props.widgetPresentationMode;
    }

    /**  Applies mode-specfic overlays for dashboard view and edit mode controls.*/
    private renderOverlay() {
        switch (this.props.widgetPresentationMode) {
            case WidgetPresentationMode.embeddedView: // Embedded mode users can pin and lightbox.
                return this.renderEmbeddedOverlay();
            case WidgetPresentationMode.dashboardEditView:
                return this.renderEditOverlay();
            case WidgetPresentationMode.dashboardView:
                return this.renderViewOverlay();
            case WidgetPresentationMode.dashboardConfigPreview:
                return <div className="interaction-overlay" />;
            case WidgetPresentationMode.lightboxView:
            default:
                return null;
        }
    }

    private renderEmbeddedOverlay(): JSX.Element {
        const onCopyToDashboard =
            this.props.widgetHostParentProps && this.props.widgetHostParentProps!.onCopyWidget ? this.onCopyToDashboard : undefined;
        return (
            <WidgetHostOverlay
                isPointerInsideParent={this.state.isMouseInsideWidget}
                canCopyWidget={this.canCopyWidget()}
                useDarkOverlay={this.state.widgetContextMenuColorMode === WidgetContextMenuColorMode.Dark}
                customMenuItems={this.state.customMenuItems}
                onCopyToDashboard={onCopyToDashboard}
                shouldFocusMoreActionsButtonByDefault={this.props.shouldFocusMoreActionsButtonByDefault}
            />
        );
    }

    private renderEditOverlay(): JSX.Element {
        return (
            <WidgetHostEditOverlay
                canConfigureWidget={this.canConfigureWidget()}
                onConfigureWidget={this.onConfigureWidget}
                onDeleteWidget={this.onDeleteWidget}
                ref={(ref: WidgetHostEditOverlay) => { this.editOverlayElement = ref }}
                onKeyDown={(event: React.KeyboardEvent, isFirstButton: boolean) => {
                    if (isFirstButton && event.shiftKey && event.keyCode === KeyCode.tab) {
                        event.preventDefault();
                        this.contentContainerElement.focus();
                    }
                }}
            />
        );    
    }

    private renderViewOverlay(): JSX.Element {
        return (
            <WidgetHostOverlay
                isPointerInsideParent={this.state.isMouseInsideWidget}
                allowLightbox={this.canUseLightbox()}
                canConfigureWidget={this.canConfigureWidget()}
                canDeleteWidget={this.canDeleteWidget()}
                canCopyWidget={this.canCopyWidget()}
                canViewWidgetInformation={this.canViewWidgetInformation()}
                useDarkOverlay={this.state.widgetContextMenuColorMode === WidgetContextMenuColorMode.Dark}
                customMenuItems={this.state.customMenuItems}
                onConfigureWidget={this.onConfigureWidget}
                onDeleteWidget={this.onDeleteWidget}
                onLightbox={this.onShowLightbox}
                onCopyToDashboard={this.onCopyToDashboard}
                onViewWidgetInformation={this.onViewWidgetInformation}
                shouldFocusMoreActionsButtonByDefault={this.props.shouldFocusMoreActionsButtonByDefault}
            />
        );
    }

    private onCopyToDashboard = (moreActionsButton: Button | null): void => {
        this._moreActionsButton = moreActionsButton;
        this.context.pageContext.getService<IDashboardsPanelManager>("IDashboardsPanelManager").copyWidget(this.props.widgetProps, this.onCopyWidget, this._onAddDashboardPanelDismiss, { parentPageName: this.props.parentPageName });
    };

    private _onAddDashboardPanelDismiss = (): void => {
        if (this._moreActionsButton) {
            this._moreActionsButton.focus();
        }
    }

    private onViewWidgetInformation = (): void => {
        if (this.canViewWidgetInformation()) {
            this.openInformationLink();
            publishWidgetHostInteraction(this.context.pageContext, "WidgetHost-ViewInformation", this.props.widgetProps.contributionId);
        }
    };

    private async openInformationLink() {
        const featureName = "WidgetHost-OpenConfirmationLink";
        const contributionId = this.props.widgetProps.contributionId;
        const queryCache = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        // Pull widget catalog. Ignore project filtering, to allow docs to be reached  for widgets of disabled verticals.
        const query = new WidgetCatalogQuery(undefined);
        const queryResult = await queryCache.getCacheableQueryResult(query);
        const widgetCatalog = queryResult.catalogEntries;
        const catalogIndex = widgetCatalog.findIndex((widgetEntry) => {
            return widgetEntry.contributionId === this.props.widgetProps.contributionId;
        });
        if (catalogIndex >= 0) {
            const infoUri = widgetCatalog[catalogIndex].catalogInfoURI;
            if (infoUri != null) {
                const target = new Uri(infoUri);
                const newWindow = window.open(target.absoluteUri, "_blank", "noopener noreferrer");
                if (newWindow) {
                    window.opener = null; // Guard against pre-modern versions of Edge 40 and IE11, which do not enforce noopener
                }
            } else {
                traceWidgetHostWarning(this.context.pageContext, featureName, `Information link for contribution was not found.`, contributionId);
            }
        } else {
            traceWidgetHostWarning(this.context.pageContext, featureName, `Widget contribution was not found.`, contributionId);
        }
    }

    private onConfigureWidget = (): void => {
        if (this.props.userCanConfigure && this.props.widgetHostParentProps && this.props.widgetHostParentProps.onConfigureWidget) {
            this.props.widgetHostParentProps.onConfigureWidget(this.props.widgetProps);
        }
    };

    private onCopyWidget = (widgetCopyProps: WidgetCopyProps): void => {
        if (this.props.widgetHostParentProps && this.props.widgetHostParentProps.onCopyWidget) {
            this.props.widgetHostParentProps.onCopyWidget(widgetCopyProps);
        }
    };

    private onDeleteWidget = (): void => {
        if (this.props.userCanConfigure && this.props.widgetHostParentProps && this.props.widgetHostParentProps.onDeleteWidget) {
            this.props.widgetHostParentProps.onDeleteWidget(this.props.widgetProps);
        }
    };

    private onWidgetLoadComplete = (loadedEventInfo?: LoadedEventInfo): void => {
        // If caller doesn't pass any eventInfo, that is recognized as success.
        let eventInfo: LoadedEventInfo = loadedEventInfo || { outcome: LoadingStatus.success, outcomeDescription: undefined };

        // If the caller passes an object in as eventInfo, but it doesn't define an outcome property, it is treated as a failure.
        // Whatever information was provided, we naively re-pack as a description, and let the telemetry layer do the detailed interpretation.
        eventInfo = this.normalizeEventInfo(eventInfo);

        // React to the first load event from the widget in this render cycle.
        if (this.state.widgetLoadStatus === LoadingStatus.loading && eventInfo.outcome !== LoadingStatus.loading && !this.isUnmounted) {
            // Convert from widget load outcomes to Host outcomes
            const hostOutcome = eventInfo.outcome === LoadingStatus.success ? WidgetHostLoadOutcome.Success : WidgetHostLoadOutcome.HandledFailure;

            this.onFirstLoadTransition(hostOutcome);

            if (eventInfo.outcome === LoadingStatus.failure && !this.state.hasSignaledFirstLoad) {
                publishWidgetHostLoadFailed(this.context.pageContext, this.getWidgetTelemetryInfo(), {
                    errorType: WidgetHostLoadOutcome[WidgetHostLoadOutcome.HandledFailure],
                    error: eventInfo.outcomeDescription,
                    extendedDiagnosticDetail: eventInfo.extendedDiagnosticDetail
                });
            }

            this.setState({
                hasSignaledFirstLoad: true,
                widgetLoadStatus: eventInfo.outcome,
                widgetLoadOutcomeDescription:
                    eventInfo.outcomeDescription != null ? eventInfo.outcomeDescription.toString() : Resources.WidgetError_NoContent
            });
        }
    };

    private setMenuColorMode = (widgetContextMenuColorMode: WidgetContextMenuColorMode): void => {
        this.setState({
            widgetContextMenuColorMode: widgetContextMenuColorMode
        });
    };

    private setCustomContextMenuItems = (customMenuItems: IVssContextualMenuItem[]): void => {
        this.setState({
            customMenuItems: customMenuItems
        });
    };

    /**
     * Reports to the parent that the first load was done. Reloads are not reported on this flow
     */
    private onFirstLoadTransition(outcome: WidgetHostLoadOutcome) {
        this.stopTimer();

        if (
            !this.state.hasSignaledFirstLoad &&
            this.props.widgetHostParentProps &&
            this.props.widgetHostParentProps.onWidgetLoaded &&
            this.state.widgetLoadStatus == LoadingStatus.loading
        ) {
            this.props.widgetHostParentProps.onWidgetLoaded({
                outcome: outcome,
                widget: this.props.widgetProps
            });
        }
    }

    /** For Lightbox handling */
    private onMouseEnterWidget = (): void => {
        this.setState({
            isMouseInsideWidget: true
        });
    };

    /** For Lightbox handling */
    private onMouseLeaveWidget = (): void => {
        this.setState({
            isMouseInsideWidget: false
        });
    };

    private stopTimer(): void {
        this.clearTimeout(this.hostTimeoutId);
    }

    private onKeydown(event: React.KeyboardEvent) {
        if (this.props.widgetPresentationMode == WidgetPresentationMode.dashboardEditView) {
            if(this.props.widgetHostParentProps && this.props.widgetHostParentProps.onWidgetMoved){
                const updatedWidgetGridPositions = getUpdatedWidgetPosition(event, this.props.widgetProps);
                if(updatedWidgetGridPositions.length > 0 ){
                    this.props.widgetHostParentProps.onWidgetMoved(updatedWidgetGridPositions);
                }
                else {
                    if (event.keyCode === KeyCode.tab && !event.shiftKey) {
                        event.preventDefault();
                        this.editOverlayElement.focus();
                    }
                }
            }          
        }
    }
}
